import { PlusIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useToday } from "../../../hooks/track/useToday";
import { useClientTranslations } from "../../../lib/i18n/client";
import type { Event } from "../../../types/event";
import { ComingUpCard } from "../ComingUpCard";
import styles from "./ComingUpFeed.module.css";

interface ComingUpFeedProps {
	events: Event[] | undefined;
	isLoading: boolean;
	onEventClick: (event: Event) => void;
	/** Phase 37 wiring (D-16) — TrackPage owns AddEventSheet open state. */
	onAddEvent?: () => void;
}

/**
 * ComingUpFeed (CUP-01..04 + D-14 visual tabs).
 *
 * Renders the Coming Up section: title + (+) button (CUP-01 → Phase 37 wired
 * via `onAddEvent` prop), Urgent / Today filter tabs (D-14 — i18n-keyed,
 * visual-only in Phase 36; Phase 37 wires real filtering in a later plan),
 * skeleton state (CUP-04), empty state (CUP-04), and a date-sorted list of
 * `ComingUpCard`s (CUP-02, CUP-03).
 */
export function ComingUpFeed({
	events,
	isLoading,
	onEventClick,
	onAddEvent,
}: ComingUpFeedProps) {
	const { t } = useClientTranslations("track");
	const { todayIso } = useToday();
	const [activeTab, setActiveTab] = useState<"urgent" | "today">("today");

	const sortedEvents = events
		? [...events].sort((a, b) => a.dateTime.localeCompare(b.dateTime))
		: [];
	const visibleEvents = sortedEvents.filter((e) =>
		activeTab === "today"
			? e.dateTime.slice(0, 10) === todayIso
			: e.dateTime.slice(0, 10) !== todayIso,
	);

	return (
		<section className={styles.section}>
			<div className={styles.header}>
				<h2 className={styles.title}>{t("comingUp.title")}</h2>
				<button
					type="button"
					className={styles.addBtn}
					aria-label={t("addEvent.ariaLabel")}
					onClick={onAddEvent}
				>
					<PlusIcon className={styles.plusIcon} />
				</button>
			</div>

			{/* D-02 (Phase 38.1): Today/Urgent tabs filter the events list by local-date.
			    WAI-ARIA tablist pattern (role="tablist" / tabpanel / roving tabindex)
			    remains deferred — aria-pressed on each button is the current contract. */}
			<div className={styles.tabs}>
				<button
					type="button"
					aria-pressed={activeTab === "urgent"}
					className={`${styles.tab} ${activeTab === "urgent" ? styles.tabActive : ""}`}
					onClick={() => setActiveTab("urgent")}
				>
					{t("comingUp.tabs.urgent")}
				</button>
				<button
					type="button"
					aria-pressed={activeTab === "today"}
					className={`${styles.tab} ${activeTab === "today" ? styles.tabActive : ""}`}
					onClick={() => setActiveTab("today")}
				>
					{t("comingUp.tabs.today")}
				</button>
			</div>

			{isLoading && (
				<div className={styles.skeletonList} aria-busy="true">
					<div className={styles.skeletonCard} />
					<div className={styles.skeletonCard} />
					<div className={styles.skeletonCard} />
				</div>
			)}

			{!isLoading && visibleEvents.length === 0 && (
				<div className={styles.emptyState}>
					<p className={styles.emptyTitle}>{t("comingUp.empty.title")}</p>
					<p className={styles.emptyBody}>{t("comingUp.empty.body")}</p>
				</div>
			)}

			{!isLoading && visibleEvents.length > 0 && (
				<ul className={styles.list}>
					{visibleEvents.map((event) => (
						<li key={event.id}>
							<ComingUpCard event={event} onClick={onEventClick} />
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
